import {
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconLoader2,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import MainCard from './MainCard';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  CONFIRM_PHRASE,
  formatMb,
  formatNumber,
  summariseStoreLabel,
} from '@/lib/spar-mobile/storeOwnershipChange';
import { useStoreOwnershipChangePanel } from '@/hooks/useStoreOwnershipChangePanel';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function StoreOwnershipChangePanel() {
  const { hasWriteAccess } = useAuth();
  const canWrite = hasWriteAccess('spar-mobile');
  const {
    discoveryError,
    retryRoms,
    refreshPreview,
    storeSearchTerm,
    setStoreSearchTerm,
    storeSuggestions,
    storeSearchLoading,
    selectedOldStore,
    handleSelectStore,
    handleClearStore,
    preview,
    previewLoading,
    previewError,
    externalId,
    setExternalId,
    newStoreName,
    setNewStoreName,
    contactFirstName,
    setContactFirstName,
    contactLastName,
    setContactLastName,
    contactEmailAddress,
    setContactEmailAddress,
    newRomId,
    setNewRomId,
    romPopoverOpen,
    setRomPopoverOpen,
    romSearchTerm,
    setRomSearchTerm,
    romsLoading,
    filteredRomOptions,
    selectedRom,
    externalIdValid,
    formValid,
    confirmOpen,
    setConfirmOpen,
    confirmText,
    setConfirmText,
    submitting,
    submitError,
    result,
    openConfirm,
    handleSubmit,
  } = useStoreOwnershipChangePanel(canWrite);

  return (
    <div className="p-6 space-y-6">
      {discoveryError && (
        <Alert variant="destructive">
          <AlertDescription>
            {discoveryError}
            <Button
              type="button"
              variant="outline"
              onClick={() => void retryRoms()}
              disabled={romsLoading}
            >
              Retry ROM lookup
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {!canWrite && (
        <Alert>
          <AlertDescription>
            You have read-only access. You can preview ownership changes;
            execution requires write access.
          </AlertDescription>
        </Alert>
      )}
      {submitError && !confirmOpen && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}
      {result && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <AlertDescription>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 font-semibold">
                <IconCheck className="size-4" />
                Ownership change complete
              </div>
              <div className="text-sm">
                Old store <strong>#{result.oldStoreId}</strong> was migrated to
                new store partner{' '}
                <strong>
                  #{result.newStoreId}
                  {result.newStore?.name ? ` (${result.newStore.name})` : ''}
                </strong>
                .
              </div>
              <div className="text-sm">
                Moved {formatNumber(result.customersMoved)} customers,{' '}
                {formatNumber(result.simCardsMoved)} SIM cards, and{' '}
                {formatNumber(result.userRolesMoved)} portal user roles.
              </div>
              <div className="text-sm">
                {result.promoDataTransferSkipped ? (
                  <>
                    No promo data balance was transferred (old store balance was{' '}
                    {formatMb(0)}).
                  </>
                ) : (
                  <>
                    Transferred {formatMb(result.promoDataBalanceMoved)} of
                    promo data balance from the old store to the new one.
                  </>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <MainCard>
        <h3 className="text-base font-semibold">
          Step 1 — Select the existing store
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5 mb-4">
          Search for the SPAR Mobile store whose ownership is changing. Only
          active stores are listed.
        </p>

        {selectedOldStore ? (
          <div className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 text-sm">
                <div className="font-semibold">
                  {selectedOldStore.name}{' '}
                  <Badge variant="secondary" className="ml-1">
                    id #{selectedOldStore.id}
                  </Badge>
                </div>
                <div className="text-muted-foreground">
                  External ID:{' '}
                  <strong>{selectedOldStore.external_id ?? '—'}</strong> ·
                  Terminal:{' '}
                  <strong>
                    {selectedOldStore.cyclepay_terminal_id ?? '—'}
                  </strong>
                </div>
                <div className="text-muted-foreground">
                  Current ROM:{' '}
                  <strong>{selectedOldStore.rom_name ?? '—'}</strong> · DC:{' '}
                  <strong>{selectedOldStore.dc_name ?? '—'}</strong>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearStore}
                disabled={submitting}
              >
                <IconX className="size-4" />
                Change
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ownership-store-search">
              Search by name, external ID, or terminal ID
            </Label>
            <Popover
              open={
                storeSearchTerm.trim().length >= 2 &&
                (storeSuggestions.length > 0 || storeSearchLoading)
              }
              onOpenChange={() => {}}
            >
              <PopoverTrigger
                nativeButton={false}
                render={<div className="relative" />}
              >
                <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="ownership-store-search"
                  className="pl-9"
                  placeholder="Start typing store name, external ID, or terminal ID..."
                  value={storeSearchTerm}
                  onChange={(e) => setStoreSearchTerm(e.target.value)}
                />
                {storeSearchLoading && (
                  <IconLoader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                initialFocus={false}
              >
                <Command shouldFilter={false}>
                  <CommandList>
                    {storeSearchLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <IconLoader2 className="h-4 w-4 animate-spin" />
                        <span className="ml-2 text-sm">
                          Searching stores...
                        </span>
                      </div>
                    ) : storeSuggestions.length === 0 ? (
                      <CommandEmpty>
                        {storeSearchTerm.trim().length < 2
                          ? 'Type at least 2 characters'
                          : 'No stores found'}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {storeSuggestions.map((option) => (
                          <CommandItem
                            key={option.id}
                            value={String(option.id)}
                            onSelect={() => handleSelectStore(option)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {summariseStoreLabel(option)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ROM: {option.rom_name ?? '—'} · DC:{' '}
                                {option.dc_name ?? '—'}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </MainCard>

      {selectedOldStore && (
        <MainCard>
          <h3 className="text-base font-semibold">
            Step 2 — Review what will move
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">
            Current customer attribution, store-owned SIM references, and portal
            user role assignments below will move to the new store partner,
            along with attached standalone agent partners. Historical
            transactions, commissions, and completed or failed rewards remain
            with the old store, which will become{' '}
            <Badge variant="secondary">inactive</Badge>. Any issues listed below
            must be resolved before the change can proceed.
          </p>

          {previewError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{previewError}</AlertDescription>
            </Alert>
          )}

          {previewLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <IconLoader2 className="size-4 animate-spin" />
              Loading preview...
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            className="mb-4"
            onClick={() => void refreshPreview()}
            disabled={previewLoading || submitting}
          >
            Refresh preview
          </Button>

          {preview && !previewLoading && (
            <div className="space-y-4">
              {preview.blockers.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <div>Resolve these issues before changing ownership:</div>
                    <ul className="list-disc pl-5">
                      {preview.blockers.map((blocker, index) => (
                        <li key={index}>{blocker}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border border-border/60 p-3">
                  <div className="text-xs uppercase text-muted-foreground">
                    Customers
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatNumber(preview.counts.customers)}
                  </div>
                </div>
                <div className="rounded-lg border border-border/60 p-3">
                  <div className="text-xs uppercase text-muted-foreground">
                    SIM cards
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatNumber(preview.counts.simCards)}
                  </div>
                </div>
                <div className="rounded-lg border border-border/60 p-3">
                  <div className="text-xs uppercase text-muted-foreground">
                    Portal user roles
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatNumber(preview.counts.appUserRoles)}
                  </div>
                </div>
                <div className="rounded-lg border border-border/60 p-3">
                  <div className="text-xs uppercase text-muted-foreground">
                    Available promo data
                  </div>
                  <div
                    className={cn(
                      'text-2xl font-semibold mt-1',
                      preview.promoDataBalance.balance <= 0 &&
                        'text-muted-foreground',
                    )}
                  >
                    {formatMb(preview.promoDataBalance.balance)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Reserved: {formatMb(preview.promoDataBalance.reserved)}
                  </div>
                  {preview.promoDataBalance.balance <= 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Nothing to transfer
                    </div>
                  )}
                </div>
              </div>

              {preview.users.length > 0 ? (
                <div>
                  <div className="text-sm font-semibold mb-2">
                    Users that will be moved
                  </div>
                  <div className="rounded-lg border border-border/60 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Username</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead className="text-right">Active?</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.users.map((user) => (
                          <TableRow
                            key={`${user.appUserId}-${user.role ?? ''}`}
                          >
                            <TableCell className="font-medium">
                              {user.username ?? '—'}
                            </TableCell>
                            <TableCell>{user.name ?? '—'}</TableCell>
                            <TableCell>
                              {user.role ? (
                                <Badge variant="secondary">{user.role}</Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {user.isActive ? (
                                <Badge variant="success">active</Badge>
                              ) : (
                                <Badge variant="outline">inactive</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No portal users currently linked to this store.
                </div>
              )}
            </div>
          )}
        </MainCard>
      )}

      {selectedOldStore && preview && !previewLoading && !previewError && (
        <MainCard>
          <h3 className="text-base font-semibold">
            Step 3 — New store partner details
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">
            A new Store partner will be created with the details below and
            become the new owner of the store. Enter the successor store name.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="ownership-store-name">New store name *</Label>
              <Input
                id="ownership-store-name"
                value={newStoreName}
                onChange={(e) => setNewStoreName(e.target.value)}
                maxLength={255}
                disabled={submitting}
                aria-invalid={!newStoreName.trim()}
              />
              {!newStoreName.trim() && (
                <span className="text-xs text-destructive">
                  Enter the new store name.
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="ownership-rom-trigger">
                New ROM <span className="text-destructive">*</span>
              </Label>
              <Popover open={romPopoverOpen} onOpenChange={setRomPopoverOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      type="button"
                      id="ownership-rom-trigger"
                      variant="outline"
                      role="combobox"
                      aria-expanded={romPopoverOpen}
                      disabled={submitting || romsLoading}
                      className="w-full justify-between font-normal"
                    />
                  }
                >
                  <span
                    className={cn(
                      'truncate',
                      !selectedRom && 'text-muted-foreground',
                    )}
                  >
                    {romsLoading
                      ? 'Loading ROMs...'
                      : selectedRom
                        ? `${selectedRom.name}${selectedRom.dc_name ? ` · ${selectedRom.dc_name}` : ''}`
                        : 'Select a ROM'}
                  </span>
                  <IconChevronDown className="size-4 opacity-50 shrink-0" />
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search ROMs by name or DC..."
                      value={romSearchTerm}
                      onValueChange={setRomSearchTerm}
                    />
                    <CommandList>
                      {filteredRomOptions.length === 0 ? (
                        <CommandEmpty>
                          {romsLoading
                            ? 'Loading...'
                            : 'No matching ROMs found.'}
                        </CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {filteredRomOptions.map((rom) => (
                            <CommandItem
                              key={rom.id}
                              value={String(rom.id)}
                              onSelect={() => {
                                setNewRomId(rom.id);
                                setRomPopoverOpen(false);
                                setRomSearchTerm('');
                              }}
                            >
                              <div className="flex w-full items-center justify-between gap-2">
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate font-medium">
                                    {rom.name}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    DC: {rom.dc_name ?? '—'}
                                  </span>
                                </div>
                                {selectedRom?.id === rom.id && (
                                  <IconCheck className="size-4 text-primary shrink-0" />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedRom && (
                <span className="text-xs text-muted-foreground">
                  DC inherited from ROM:{' '}
                  <strong>{selectedRom.dc_name ?? '—'}</strong>
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ownership-external-id">
                Store code (7-8 digits){' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ownership-external-id"
                inputMode="numeric"
                placeholder="e.g. 8804471"
                value={externalId}
                onChange={(e) =>
                  setExternalId(e.target.value.replace(/[^0-9]/g, ''))
                }
                disabled={submitting}
              />
              {externalId.length > 0 && !externalIdValid && (
                <span className="text-xs text-destructive">
                  Must be 7-8 digits.
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ownership-contact-first-name">
                Retailer first name
              </Label>
              <Input
                id="ownership-contact-first-name"
                value={contactFirstName}
                onChange={(e) => setContactFirstName(e.target.value)}
                maxLength={100}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ownership-contact-last-name">
                Retailer last name
              </Label>
              <Input
                id="ownership-contact-last-name"
                value={contactLastName}
                onChange={(e) => setContactLastName(e.target.value)}
                maxLength={100}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ownership-contact-email">Contact email</Label>
              <Input
                id="ownership-contact-email"
                type="email"
                value={contactEmailAddress}
                onChange={(e) => setContactEmailAddress(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          {submitError && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {preview && !previewLoading && preview.blockers.length > 0 && (
            <Alert
              variant="destructive"
              className="mt-4"
              id="ownership-execution-blockers"
            >
              <AlertDescription>
                <div>Ownership change is blocked. Resolve these issues:</div>
                <ul className="list-disc pl-5">
                  {preview.blockers.map((blocker, index) => (
                    <li key={index}>{blocker}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={openConfirm}
              disabled={!formValid || submitting}
              aria-describedby={
                preview && !previewLoading && preview.blockers.length > 0
                  ? 'ownership-execution-blockers'
                  : undefined
              }
              className={cn(
                formValid ? 'bg-destructive hover:bg-destructive/90' : '',
              )}
            >
              <IconArrowRight className="size-4" />
              Review and execute ownership change
            </Button>
            <span className="text-xs text-muted-foreground">
              You will be asked to type <strong>{CONFIRM_PHRASE}</strong> to
              confirm.
            </span>
          </div>
        </MainCard>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconAlertTriangle className="size-5 text-destructive" />
              Confirm store ownership change
            </DialogTitle>
            <DialogDescription>
              This will create a new store partner and re-point all linked
              current memberships and eligible inventory. Historical records
              stay with the old store. The action cannot be undone from the UI.
            </DialogDescription>
          </DialogHeader>

          {selectedOldStore && preview && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-border/60 p-3 space-y-1">
                <div className="font-semibold">
                  From (will be marked inactive)
                </div>
                <div>
                  {selectedOldStore.name}{' '}
                  <span className="text-muted-foreground">
                    (id #{selectedOldStore.id}, external{' '}
                    {selectedOldStore.external_id ?? '—'})
                  </span>
                </div>
              </div>
              <div className="rounded-md border border-border/60 p-3 space-y-1">
                <div className="font-semibold">To (new store partner)</div>
                <div>
                  {newStoreName.trim()}{' '}
                  <span className="text-muted-foreground">
                    (external {externalId.trim() || '—'}, ROM{' '}
                    {selectedRom?.name ?? '—'})
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Historical records retain the old store name.
                </div>
                <div>
                  Retailer contact:{' '}
                  {[contactFirstName.trim(), contactLastName.trim()]
                    .filter(Boolean)
                    .join(' ') || '—'}
                  {contactEmailAddress.trim() && (
                    <> · {contactEmailAddress.trim()}</>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Contact details do not create a login or replace existing
                  portal users.
                </div>
              </div>
              <div>
                Will move {formatNumber(preview.counts.customers)} customers,{' '}
                {formatNumber(preview.counts.simCards)} SIM cards, and{' '}
                {formatNumber(preview.counts.appUserRoles)} portal user roles.
              </div>
              <div>
                {preview.promoDataBalance.balance > 0 ? (
                  <>
                    Available promo data of{' '}
                    <strong>
                      {formatMb(preview.promoDataBalance.balance)}
                    </strong>{' '}
                    will be transferred to the new store.
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    No promo data balance to transfer (current balance is{' '}
                    {formatMb(preview.promoDataBalance.balance)}).
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ownership-confirm-text">
                  Type <strong>{CONFIRM_PHRASE}</strong> to confirm
                </Label>
                <Input
                  id="ownership-confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>
              {submitError && (
                <Alert variant="destructive">
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={
                !formValid || submitting || confirmText !== CONFIRM_PHRASE
              }
            >
              {submitting ? (
                <>
                  <IconLoader2 className="size-4 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <IconCheck className="size-4" />
                  Execute ownership change
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
